import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Play, Disc, ArrowRight } from "lucide-react";
import { UserHomepageService } from "../services/UserHomepageServices";

export default function FeaturedAlbums({ albums = [], loading = false }) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // ── Filter state ───────────────────────────────────────────────────────────
  const [activeType, setActiveType] = useState("ALL");
  const [filteredAlbums, setFilteredAlbums] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);

  // Standard categories for discography filtering
  const STANDARD_CATEGORIES = ["ALL", "Band Leader", "Sideman", "Producer"];
  const types = Array.from(new Set([...STANDARD_CATEGORIES, ...albums.map((a) => a.category || a.type).filter(Boolean)]));

  // On mount or when albums change, seed the displayed list with up to 4 items.
  useEffect(() => {
    setFilteredAlbums(albums.slice(0, 4));
    setActiveType("ALL");
  }, [albums]);

  // ── Intersection observer for fade-in ─────────────────────────────────────
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) {
          setIsVisible(true);
          observer.unobserve(section);
        }
      },
      { threshold: [0, 0.1, 0.2], rootMargin: "50px" },
    );

    observer.observe(section);

    const rect = section.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setIsVisible(true);
    }

    return () => {
      if (section) observer.unobserve(section);
      observer.disconnect();
    };
  }, []);

  // ── Category tab handler ───────────────────────────────────────────────────
  const handleTypeChange = useCallback(
    async (type) => {
      if (type === activeType) return;
      setActiveType(type);
      setFilterLoading(true);
      try {
        // "ALL" → client-side slice from existing data (no extra request)
        if (type === "ALL") {
          setFilteredAlbums(albums.slice(0, 4));
        } else {
          // Specific type/category → ask backend to return up to 4 matching items
          const result = await UserHomepageService.getDiscographyByType(type);
          setFilteredAlbums(Array.isArray(result) ? result.slice(0, 4) : []);
        }
      } catch (err) {
        console.error("[FeaturedAlbums] filter error:", err);
        // Graceful fallback: client-side filter from pre-loaded albums
        const fallback =
          type === "ALL"
            ? albums
            : albums.filter((a) => (a.category || a.type)?.toLowerCase() === type.toLowerCase());
        setFilteredAlbums(fallback.slice(0, 4));
      } finally {
        setFilterLoading(false);
      }
    },
    [activeType, albums],
  );

  // ── Skeleton card ──────────────────────────────────────────────────────────
  const SkeletonCard = () => (
    <div className="bg-white/10 border border-white/20 overflow-hidden animate-pulse">
      <div className="aspect-square bg-white/10" />
      <div className="p-6 space-y-4">
        <div className="flex justify-between items-start">
          <div className="h-6 bg-white/20 w-2/3 rounded" />
          <div className="h-4 bg-white/20 w-12 rounded" />
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-white/10 w-full rounded" />
          <div className="h-3 bg-white/10 w-5/6 rounded" />
        </div>
        <div className="h-10 bg-white/20 w-full rounded pt-2" />
      </div>
    </div>
  );

  const isRenderLoading = loading || filterLoading;
  const displayAlbums = filteredAlbums;

  return (
    <section
      ref={sectionRef}
      id="discography"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-black text-white overflow-hidden"
      style={{ fontFamily: "Montserrat, sans-serif" }}
    >
      <div className="max-w-[1400px] mx-auto">

        {/* Section header */}
        <div
          className={`text-center mb-10 sm:mb-14 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
        >
          <h2
            className="text-3xl sm:text-4xl lg:text-6xl font-normal mb-4 sm:mb-6"
            style={{
              fontFamily: "Playfair Display, Georgia, serif",
              fontWeight: 300,
              letterSpacing: "-0.01em",
            }}
          >
            Discography
          </h2>
          <div className="w-16 sm:w-24 h-[1px] bg-white/80 mx-auto" />
        </div>

        {/* ── Dynamic Category Tabs ─────────────────────────────────────────── */}
        {!loading && types.length > 1 && (
          <div
            className={`flex flex-wrap justify-center gap-3 mb-10 sm:mb-14 transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
              }`}
          >
            {types.map((type) => (
              <button
                key={type}
                onClick={() => handleTypeChange(type)}
                disabled={filterLoading}
                className={`px-4 py-1.5 text-[10px] font-bold tracking-widest uppercase border transition-all duration-300 disabled:opacity-50 ${activeType === type
                    ? "bg-white text-black border-white"
                    : "bg-transparent text-white/70 border-white/30 hover:border-white hover:text-white"
                  }`}
              >
                {type}
              </button>
            ))}
          </div>
        )}

        {/* Albums grid */}
        <div className="min-h-[400px]">
          {isRenderLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
              {[1, 2, 3, 4].map((i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : displayAlbums.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 lg:gap-10">
              {displayAlbums.map((album, index) => (
                <article
                  key={album.id || index}
                  className={`album-card group relative bg-white/5 border border-white/10 overflow-hidden transition-all duration-700 hover:bg-white/10 hover:border-white/20 hover:-translate-y-2 hover:shadow-[0_20px_60px_rgba(255,255,255,0.1)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
                    }`}
                  style={{ transitionDelay: isVisible ? `${index * 120}ms` : "0ms" }}
                >
                  {/* Album cover */}
                  <div className="relative aspect-square overflow-hidden bg-black/20">
                    {album.cover_path ? (
                      <img
                        src={album.cover_path}
                        alt={`${album.title} album cover`}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-white/5">
                        <Disc size={64} className="text-white/10" />
                      </div>
                    )}

                    {/* Play overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <Link
                        to={album.spotify_url || album.apple_music_url || "#"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white text-black flex items-center justify-center transform scale-75 group-hover:scale-100 transition-transform duration-300 hover:bg-white/90 touch-manipulation"
                        aria-label={`Listen to ${album.title}`}
                      >
                        <Play size={22} fill="black" />
                      </Link>
                    </div>

                    {/* Type badge */}
                    <div className="absolute top-3 sm:top-4 right-3 sm:right-4 bg-jazz-brown text-white px-2.5 sm:px-3 py-1 text-[10px] sm:text-xs font-semibold tracking-wider uppercase">
                      {album.category || album.type}
                    </div>
                  </div>

                  {/* Album info */}
                  <div className="p-5 sm:p-6">
                    <div className="flex items-baseline justify-between mb-2 sm:mb-3 gap-2">
                      <h3
                        className="text-lg sm:text-xl font-medium text-white group-hover:text-white transition-colors duration-300"
                        style={{ fontFamily: "Playfair Display, Georgia, serif" }}
                      >
                        {album.title}
                      </h3>
                      <span className="text-sm text-white font-medium flex-shrink-0">
                        {album.year}
                      </span>
                    </div>

                    <p
                      className="text-sm text-white/85 leading-relaxed line-clamp-2 h-10"
                      style={{ fontFamily: "Crimson Pro, Georgia, serif", lineHeight: "1.6" }}
                    >
                      {album.description}
                    </p>

                    <div className="mt-5 sm:mt-6 pt-4 border-t border-white/10">
                      <Link
                        to={`/discography/${album.id}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-white hover:text-white/80 transition-colors duration-200 touch-manipulation py-1"
                        style={{ letterSpacing: "0.5px" }}
                      >
                        VIEW DETAILS
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="col-span-full py-20 text-center border border-dashed border-white/20 rounded-xl">
              <Disc size={48} className="mx-auto text-white/40 mb-4" />
              <p className="text-white/80 font-serif italic">No releases found.</p>
            </div>
          )}
        </div>

        {/* View all link */}
        <div
          className={`text-center mt-12 sm:mt-16 transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
        >
          <Link
            to="/discography"
            className="inline-block border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 hover:bg-white hover:text-black transition-all duration-300 touch-manipulation"
            style={{ fontFamily: "Montserrat, sans-serif", fontSize: "13px", fontWeight: 600, letterSpacing: "1px" }}
          >
            VIEW FULL DISCOGRAPHY
          </Link>
        </div>
      </div>

      <style jsx="true">{`
        @media (max-width: 1023px) {
          .album-card {
            transform: none !important;
          }
        }
      `}</style>
    </section>
  );
}
